<template>
  <UiModal @close="close">
    <perfect-scrollbar
      :options="{
        handlers: scrollHandlers,
      }"
    >
      <div class="quick-view">
        <div class="images card-image">
          <SliderModelNew
            :images="allImages"
            :name="itemOffer.name"
            :quick-view="true"
            :class="['quick-slider', { visible: isVisibleSlider }]"
          />
        </div>
        <div class="text">
          <div class="text-header">
            <h1 class="text_h1">{{ itemOffer.name }}</h1>
            <div class="shop-label" :class="{ image: itemOfferLogo }">
              <ui-cpc-link
                class="p_link offerName"
                :offer="itemOffer"
                :to="click_url"
                block-type="pu-offer"
                :disabled="isPlaceOrder"
                @send-analytics="sendAnalytic(itemOffer.shop_info.id)"
              >
                <img
                  v-if="itemOfferLogo"
                  :src="itemOfferLogo"
                  :alt="itemOfferShopInfoName"
                />
                <span v-else>{{ itemOfferShopInfoName }}</span>
              </ui-cpc-link>
              <ShopRating
                :shop="itemOffer"
                :labels="['оценки', 'оценок', 'оценок']"
                is-modal
              />
            </div>
            <div class="text_specifications">
              <ul>
                <li v-for="(item, index) in specifications" :key="index">
                  {{ item.name }}: {{ item.value }}
                </li>
              </ul>
              <ui-ui-link v-if="showMore.length > 6" @click="more = !more">
                {{ more ? 'Показать еще' : 'Свернуть' }}
              </ui-ui-link>
            </div>
            <div class="price-container">
              <div v-if="itemOffer.discount" class="price-discount">
                <ui-sticker-marker-new
                  type="percent"
                  :text="itemOffer.discount.percent"
                  show-graduation
                  class="price-marker"
                />
                <ui-Currency
                  :value="itemOffer.discount.price_before"
                  class="currency--old text-body-s-book"
                >
                </ui-Currency>
              </div>
              <div class="price-value" @click.stop="copyPromo">
                <lazy-exclusive-promo
                  :coupon="exclusivePromoData"
                  :copy="alreadyCopyPromo"
                  @reset-copy="alreadyCopyPromo = false"
                >
                  <template #curshow>
                    <div class="price-value--currency">
                      <ui-currency :value="itemOffer.price" class="heading-s" />
                      <div
                        v-if="itemOffer.discount"
                        class="price-discount--mobile"
                      >
                        <ui-sticker-marker-new
                          type="percent"
                          :text="itemOffer.discount.percent"
                          show-graduation
                          class="price-marker"
                        />
                        <ui-Currency
                          :value="itemOffer.discount.price_before"
                          class="currency--old text-body-s-book"
                        >
                        </ui-Currency>
                      </div>
                    </div>
                  </template>
                </lazy-exclusive-promo>
              </div>
            </div>
            <div class="flex btns-block">
              <div class="prices-btn">
                <ui-cpc-link
                  v-if="!isPlaceOrder"
                  :offer="itemOffer"
                  :to="click_url"
                  block-type="pu-offer"
                >
                  <ui-ui-button :resize="true" mode="div" @click="close">
                    В магазин
                  </ui-ui-button>
                </ui-cpc-link>
                <ui-ui-button v-else :resize="true" @click="emit('order')">
                  Сделать заказ
                </ui-ui-button>
              </div>
            </div>
          </div>
          <div class="description">
            <div v-if="itemOffer.delivery" class="block">
              <p class="title heading-s">Доставка</p>
              <p class="text">{{ offerDeliveryCost }}</p>
              <template
                v-if="
                  itemOffer.delivery.description || offerDeliveryOptions.length
                "
              >
                <p
                  v-for="(option, index) in offerDeliveryOptions"
                  :key="index"
                  class="text"
                >
                  {{ option }}
                </p>
                <p v-if="itemOffer.delivery.description" class="text">
                  {{ itemOffer.delivery.description }}
                </p>
              </template>
              <p v-else class="text">
                {{
                  'Детальную информацию о доставке можно посмотреть на сайте магазина'
                }}
              </p>
            </div>
            <div v-if="itemOffer.pickup" class="block">
              <p class="title heading-s">Самовывоз</p>
              <p class="text">{{ offerPickupCost }}</p>
              <template
                v-if="itemOffer.pickup.description || offerPickupOptions.length"
              >
                <p
                  v-for="(option, index) in offerPickupOptions"
                  :key="index"
                  class="text"
                >
                  {{ option }}
                </p>
                <p v-if="itemOffer.pickup.description" class="text">
                  {{ itemOffer.pickup.description }}
                </p>
              </template>
              <p v-else class="text">
                {{
                  'Детальную информацию о доставке можно посмотреть на сайте магазина'
                }}
              </p>
            </div>
            <div v-if="itemOfferPaymentOptions" class="block">
              <p class="title heading-s">Оплата</p>
              <p class="text">{{ itemOfferPaymentOptions }}</p>
            </div>
            <div v-if="itemOffer.warranty" class="block">
              <p class="title heading-s">Гарантия производителя</p>
              <p class="text">{{ itemOffer.warranty }}</p>
            </div>
            <div v-if="itemOffer.sales_note" class="block">
              <p class="title heading-s">Примечание к продаже</p>
              <p class="text">{{ itemOffer.sales_note }}</p>
            </div>
            <div v-if="itemOffer.description" class="block">
              <p class="title heading-s">Описание от магазина</p>
              <p class="text">{{ itemOffer.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </perfect-scrollbar>
  </UiModal>
</template>

<script setup lang="ts">
import { API } from '~/api/api';
import type {
  DeliveryOfferModel,
  DeliveryOfferOptionsModel,
  ModelOfferItem,
} from '~/interfaces/api';

import numWord from '~/common/num-word.js';
import { useGlobalStore } from '~/stores/global';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import { getFutureDay } from '~/utils/dateUtils';
import { useTimeCalculation } from '~/composition/useTimeCalculation';

const ShopRating = defineAsyncComponent(
  () => import('~/components/ui/shop-rating/ShopRating.vue')
);
const props = defineProps({
  model: Object as () => ModelOfferItem,
});
const emit = defineEmits(['close', 'order']);

const globalStore = useGlobalStore();
const mediaStore = useMediaQueryStore();
const { $ymClickLogoEvent } = useNuxtApp();
const more = ref<boolean>(true);
const isVisibleSlider = ref<boolean>(true);
const specifications = computed(() => {
  return more.value &&
    itemOffer.value?.params &&
    itemOffer.value?.params.length > 6
    ? itemOffer.value?.params.slice(0, 6)
    : itemOffer.value?.params;
});
const itemOffer = ref<any>();
const loaded = ref<Boolean>(false);

const click_url = props.model?.click_url;
const showMore = computed(() => itemOffer.value.params || []);
const isPlaceOrder = computed(() => props.model?.cpo);
const itemOfferLogo = computed(() => itemOffer.value.shop_info?.logo);
const itemOfferPaymentOptions = computed(
  () => itemOffer.value.shop_info?.payment_options
);
const isMobile = computed(() => mediaStore.isMobile);
const scrollHandlers = ref(['click-rail', 'drag-thumb', 'keyboard', 'wheel']);
if (!isMobile.value) scrollHandlers.value.push('touch');

const itemOfferShopInfoName = computed(() => itemOffer.value?.shop_info?.name);

const alreadyCopyPromo = ref(false);
const copyPromo = () => {
  alreadyCopyPromo.value = true;
};
const exclusivePromoData = computed(
  () => itemOffer.value?.shop_info?.coupon || {}
);

onBeforeMount(() => {
  if (props.model && (props.model.image || props.model.additional_images)) {
    const allAdditionImages = props.model?.additional_images
      ? props.model?.additional_images
      : [];

    itemOffer.value = {
      ...props.model,
      additional_images: [props.model.image, ...allAdditionImages],
    };
  } else if (props.model?.id) {
    isVisibleSlider.value = false;
    API.get(`/offers/${props.model?.id}`, {
      params: { region_id: globalStore.region.id },
    })
      .then((res) => {
        itemOffer.value = res.data.additional_images
          ? {
              ...res.data,
              additional_images: [
                res.data.image,
                ...res.data.additional_images,
              ],
            }
          : res;
        setTimeout(() => {
          isVisibleSlider.value = true;
        }, 50);
      })
      .catch(() => close())
      .finally(() => (loaded.value = true));
  }
});
const allImages = computed(() => {
  return itemOffer.value?.additional_images || [];
});
const getDeliveryOptionsCost = (arr: { cost: number | null }[]) => {
  if (arr.length === 1 && arr[0].cost === null) return 'Есть';
  const min = Math.min(...arr.map((item) => (item.cost ? item.cost : 0)));
  return min ? `от\u00A0${min}\u00A0₽` : min === 0 ? 'Бесплатно' : 'Есть';
};

function formalizeDeliveryCost(delivery: DeliveryOfferModel) {
  if (delivery.cost === -1) return 'Есть';
  if (
    delivery.cost === null &&
    (!delivery.options || !delivery.options.length)
  ) {
    return 'Есть';
  }

  return delivery.cost
    ? delivery.fixed
      ? delivery.cost + '\u00A0руб.'
      : 'от\u00A0' + delivery.cost + '\u00A0руб.'
    : delivery.options && delivery.options.length
    ? getDeliveryOptionsCost(delivery.options)
    : 'Есть';
}

function close() {
  emit('close');
}

function formalizeDeliveryOptions(options: DeliveryOfferOptionsModel[]) {
  return options.map((el) => {
    const cost =
      typeof el.cost !== 'undefined' && el.cost !== null
        ? el.cost + '\u00A0руб'
        : '';

    let days = null;
    if (el.days) {
      const interval = el.days.split(/\D/).map((d) => Number(d));
      const day = interval[0];
      if (day < 3) {
        days = `${getFutureDay(day)}`;
      }
      days =
        day < 3
          ? `${getFutureDay(day)}`
          : `${el.days} ${numWord(
              interval.pop(),
              ['дней', 'день', 'дня', 'дней'],
              false
            )}`;
    }

    const orderBefore =
      el.order_before !== null ? `при заказе до ${el.order_before} часов` : '';

    return [cost, days, orderBefore].filter((el) => el).join(', ');
  });
}

const offerDeliveryCost = computed({
  get() {
    return itemOffer.value && itemOffer.value.delivery
      ? formalizeDeliveryCost(itemOffer.value.delivery)
      : 'Есть';
  },
  set() {},
});

const offerDeliveryOptions = computed({
  get() {
    return itemOffer.value && itemOffer.value.delivery
      ? formalizeDeliveryOptions(itemOffer.value.delivery.options)
      : [];
  },
  set() {},
});

const offerPickupCost = computed(() => {
  const pickup = itemOffer.value?.pickup;
  if (pickup?.description || pickup?.options?.length) return `Самовывоз есть`;
  return `Самовывоз нет`;
});

const offerPickupOptions = computed({
  get() {
    return itemOffer.value && itemOffer.value.pickup
      ? formalizeDeliveryOptions(itemOffer.value.pickup.options)
      : [];
  },
  set() {},
});

const sendAnalytic = (data: string) => {
  $ymClickLogoEvent(data);
};
useTimeCalculation();
</script>
<style lang="scss" src="./quick-view-offer.scss" scoped></style>
